/**
 * Kleine Helfer und Typen der Mail-Werkstatt (Bühne, Kommentarspalte,
 * Vorschläge, Versionen). Keine Abrufe — die stehen im Adapter.
 */
import type { Aenderung, DiffTeil } from '../../lib/zusammenarbeit';
import type { Anker, FeedbackArt, FeedbackDaten, FeedbackEintrag, Markierung, PlanungsDetail } from '../../lib/planung';

export type BuehnenModus = 'ansehen' | 'kommentieren' | 'zeichnen' | 'bearbeiten' | 'entfernen' | 'aenderungen';
export type Breite = 'computer' | 'handy';

/** Noch nicht gesendeter Kommentar: Stelle, Zitat und/oder Zeichnung aus der Mail. */
export interface Entwurf {
  anker: Anker | null;
  zitat: string | null;
  markierung: Markierung | null;
}

/** Was Spalte und Karten über die Werkstatt auslösen (Meldung + Neuladen inklusive). */
export interface WerkstattAktionen {
  /** Neuer Eintrag; liefert die id oder null bei Fehler */
  senden: (daten: FeedbackDaten, erfolg: string) => Promise<string | null>;
  /** Team: erledigt (oder mit wieder=true: wieder offen) */
  erledigen: (f: FeedbackEintrag, wieder?: boolean) => Promise<void>;
  /** Eigenen Eintrag zurückziehen (fragt vorher nach) */
  zurueckziehen: (f: FeedbackEintrag) => void;
  /** Vor allem, wonach die Mail neu lädt: bei ungespeicherten Textänderungen erst fragen (sie gehen dann verloren) */
  erstFragen: (weiter: () => void) => void;
}

/** Schlüssel des Entwurfs in der Positionsliste der Bühne. */
export const ENTWURF_ID = '__entwurf';

/** Ab dieser Breite stehen Mail und Kommentarspalte nebeneinander. */
export const BREIT_AB = 980;

/** Zeichenstift-Farben (fest — sie liegen auf der hellen Mail). */
export const STIFT_FARBEN: { wert: string; name: string }[] = [
  { wert: '#dc2626', name: 'Rot' },
  { wert: '#f59e0b', name: 'Gelb' },
  { wert: '#2563eb', name: 'Blau' },
];

export const ART_LABEL: Record<FeedbackArt, string> = {
  kommentar: 'Kommentar',
  vorschlag: 'Änderungsvorschlag',
  passt: 'Passt so',
};

/** Versendete (oder gerade versendende) Mails ändert niemand mehr. */
export function istVersendet(m: Pick<PlanungsDetail, 'status'>): boolean {
  return m.status === 'sent' || m.status === 'sending';
}

export function aenderungOffen(a: Aenderung): boolean {
  return !a.ergebnis || a.ergebnis === 'offen';
}

export function betreffOffen(f: Pick<FeedbackEintrag, 'betreff_vorschlag' | 'betreff_ergebnis'>): boolean {
  return !!f.betreff_vorschlag && !f.betreff_ergebnis;
}

/** Offene Stellen eines Vorschlags; -1 = Betreff. */
export function offeneIndizes(f: FeedbackEintrag): number[] {
  if (f.art !== 'vorschlag' || f.ergebnis) return [];
  const out: number[] = [];
  if (betreffOffen(f)) out.push(-1);
  (f.aenderungen ?? []).forEach((a, i) => { if (aenderungOffen(a)) out.push(i); });
  return out;
}

export function vorschlagOffen(f: FeedbackEintrag): boolean {
  return f.art === 'vorschlag' && !f.ergebnis;
}

/** Offen = Kommentar oder Vorschlag ohne Ergebnis — „Passt so“ ist nie offen. */
export function eintragOffen(f: FeedbackEintrag): boolean {
  return (f.art === 'kommentar' || f.art === 'vorschlag') && !f.ergebnis;
}

/**
 * Eigenen Eintrag zurückziehen geht, solange er offen ist, niemand sonst
 * geantwortet hat und das Team über keine Stelle entschieden hat (wie
 * mail_link_zurueckziehen).
 */
export function zurueckziehbar(f: FeedbackEintrag, alle: FeedbackEintrag[]): boolean {
  if (!f.ist_meins || f.ergebnis || f.betreff_ergebnis) return false;
  if ((f.aenderungen ?? []).some((a) => !aenderungOffen(a))) return false;
  return !alle.some((r) => r.antwort_auf === f.id && !r.ist_meins);
}

/** Stelle i eines Vorschlags so, wie die Datenbank sie zählt (-1 = Betreff). */
export function gespeicherteStelle(f: FeedbackEintrag, i: number): number {
  return i < 0 ? i : f.aenderungen?.[i]?.pos ?? i;
}

/** Meldung, wenn die Mail beim Speichern inzwischen geändert war (40001). */
export const KONFLIKT_TEXT = 'Die Mail wurde inzwischen geändert — ich lade den neuen Stand.';

/** Hinweis, wenn Annehmen fehlt, Ablehnen aber geht. */
export const NUR_ABLEHNEN_TEXT = 'Übernehmen dürfen Admins und wer die Mail angelegt hat — hier kannst Du ablehnen oder als erledigt markieren.';

/**
 * Platzhalter so lesbar machen wie vorschauHtml() — damit ein Vorschlag aus
 * dem Bearbeitungsmodus (Platzhalter wörtlich) in der Vorschau wiedergefunden wird.
 */
export function platzhalterLesbar(text: string): string {
  return text
    .replace(/\{\{\s*contact\.VORNAME\s*\}\}/gi, 'Alex')
    .replace(/\{\{\s*contact\.[A-Z_]+\s*\}\}/gi, '')
    .replace(/\{\{\s*(unsubscribe|mirror|update_profile)\s*\}\}/gi, '#');
}

export function aenderungLesbar(a: Aenderung): Aenderung {
  return {
    ...a,
    vorher_text: platzhalterLesbar(a.vorher_text),
    nachher_text: platzhalterLesbar(a.nachher_text),
    vorher_knoten: a.vorher_knoten.map(platzhalterLesbar),
    nachher_knoten: a.nachher_knoten ? a.nachher_knoten.map(platzhalterLesbar) : null,
    nachher_html: a.nachher_html === null ? null : platzhalterLesbar(a.nachher_html),
  };
}

/** Fehlermeldung in Klartext (die Datenbank meldet schon auf Deutsch). */
export function fehlerText(e: unknown): string {
  const m = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  if (!m) return 'Das hat nicht geklappt. Versuch es bitte noch einmal.';
  if (/failed to fetch|networkerror|load failed/i.test(m)) return 'Keine Verbindung — prüf bitte Dein Internet und versuch es noch einmal.';
  return m;
}

/** „Donnerstag, 01.10.2026, 09:14" — für title-Attribute. */
export function datumLang(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function kuerzen(text: string | null | undefined, max: number): string {
  const t = (text ?? '').trim().replace(/\s+/g, ' ');
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

export function stellenText(n: number): string {
  return n === 1 ? '1 Stelle' : `${n} Stellen`;
}

export function abschnittText(n: number): string {
  return n === 1 ? '1 Abschnitt' : `${n} Abschnitte`;
}

export function bewegungReduziert(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Erlaubte Stiftfarbe (sonst Rot) — der Wert kommt aus der Datenbank. */
export function stiftFarbe(wert: string | null | undefined): string {
  return wert && /^#[0-9a-f]{6}$/i.test(wert) ? wert : STIFT_FARBEN[0].wert;
}

/** Lange unveränderte Stücke kürzen: nur die Umgebung der Änderung zeigen. */
export function diffKuerzen(teile: DiffTeil[], umfeld = 70): { teile: DiffTeil[]; gekuerzt: boolean } {
  if (teile.length < 2) return { teile, gekuerzt: false };
  let gekuerzt = false;
  const out = teile.map((t, i) => {
    if (t.typ !== 'gleich' || t.text.length <= umfeld * 2 + 20) return t;
    const erstes = i === 0;
    const letztes = i === teile.length - 1;
    gekuerzt = true;
    if (erstes) return { typ: 'gleich' as const, text: `… ${t.text.slice(-umfeld).replace(/^\S*\s/, '')}` };
    if (letztes) return { typ: 'gleich' as const, text: `${t.text.slice(0, umfeld).replace(/\s\S*$/, '')} …` };
    return { typ: 'gleich' as const, text: `${t.text.slice(0, umfeld).replace(/\s\S*$/, '')} … ${t.text.slice(-umfeld).replace(/^\S*\s/, '')}` };
  });
  return { teile: out, gekuerzt };
}
